import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';

export default function Settings() {
  const { api, user, navigate } = useAuth();
  const [leadsPerWeek, setLeadsPerWeek] = useState(2);
  const [isPaused, setIsPaused] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [advisorData, setAdvisorData] = useState(null);

  // Load current settings
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await api('/advisors/me');
      setAdvisorData(data);
      // Format phone for display if it exists
      if (data.phone) {
        const digits = data.phone.replace(/\D/g, '');
        const local = digits.startsWith('1') ? digits.slice(1) : digits;
        setPhoneDigits(local);
        setPhone(formatPhoneDisplay(local));
      } else {
        setPhoneDigits('');
        setPhone('');
      }
      setSmsConsent(!!data.sms_consent);
      
      // Fix: properly handle 0 as a valid value (paused state)
      const currentLeadsPerWeek = typeof data.leads_per_week === 'number' ? data.leads_per_week : 2;
      setIsPaused(currentLeadsPerWeek === 0);
      setLeadsPerWeek(currentLeadsPerWeek === 0 ? 2 : currentLeadsPerWeek);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      const newValue = isPaused ? 0 : leadsPerWeek;
      
      await api('/advisors/settings', {
        method: 'PUT',
        body: JSON.stringify({
          leads_per_week: newValue
        })
      });

      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
      
      // Reload to get updated data
      loadSettings();
    } catch (error) {
      setSaveMessage('Error saving settings. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  // Format phone number for display: +1 (555) 123-4567
  const formatPhoneDisplay = (digits) => {
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value;
    
    // If they cleared the field, allow it
    if (raw === '') {
      setPhoneDigits('');
      setPhone('');
      return;
    }

    // Get only new digits from what they typed, ignoring any formatting characters
    const allDigits = raw.replace(/\D/g, '');
    
    // If the current display was longer than new input, they're deleting
    if (raw.length < phone.length) {
      // Remove last digit from our tracked digits
      const newDigits = phoneDigits.slice(0, -1);
      setPhoneDigits(newDigits);
      setPhone(formatPhoneDisplay(newDigits));
      return;
    }

    // They're adding - find the new digit(s) they typed
    // Compare all digits in new value vs what we're tracking
    const existingCount = phoneDigits.length;
    const newAllDigits = allDigits;
    
    // The new digits are whatever is beyond what formatting would produce
    let updatedDigits = phoneDigits;
    if (newAllDigits.length > existingCount) {
      const added = newAllDigits.slice(existingCount);
      updatedDigits = (phoneDigits + added).slice(0, 10);
    }

    setPhoneDigits(updatedDigits);
    setPhone(formatPhoneDisplay(updatedDigits));
  };

  const isPhoneValid = () => {
    if (!phoneDigits) return true; // Empty is OK
    return phoneDigits.length === 10;
  };

  const handleSaveProfile = async () => {
    if (!isPhoneValid()) {
      setProfileMessage('Please enter a valid 10-digit US or Canadian phone number.');
      setTimeout(() => setProfileMessage(''), 5000);
      return;
    }

    setIsSavingProfile(true);
    setProfileMessage('');

    try {
      // Send the phone as +1XXXXXXXXXX format for Twilio
      const cleanPhone = phoneDigits.length === 10 ? `+1${phoneDigits}` : '';

      await api('/advisors/profile', {
        method: 'PUT',
        body: JSON.stringify({
          firstName: advisorData.first_name,
          lastName: advisorData.last_name,
          phone: cleanPhone,
          smsConsent: smsConsent
        })
      });

      setProfileMessage('Phone settings saved!');
      setTimeout(() => setProfileMessage(''), 3000);
      loadSettings();
    } catch (error) {
      setProfileMessage('Error saving. Please try again.');
      setTimeout(() => setProfileMessage(''), 3000);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleSendTestSMS = async () => {
    setIsSendingTest(true);
    setProfileMessage('');

    try {
      const result = await api('/advisors/test-sms', { method: 'POST' });
      setProfileMessage('Test SMS sent! Check your phone.');
      setTimeout(() => setProfileMessage(''), 5000);
    } catch (error) {
      setProfileMessage(error.message || 'Failed to send test SMS. Make sure your phone number is saved.');
      setTimeout(() => setProfileMessage(''), 5000);
    } finally {
      setIsSendingTest(false);
    }
  };

  // Calculate delivery timeline based on current balance
  const calculateDelivery = () => {
    if (!advisorData || isPaused) return null;
    
    const balance = advisorData.balance_leads || 0;
    if (balance === 0) return null;
    
    const weeks = Math.ceil(balance / leadsPerWeek);
    return weeks;
  };

  const deliveryWeeks = calculateDelivery();

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-navy-600 hover:text-navy-900 flex items-center mb-4 font-body"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </button>
        
        <h1 className="font-display text-4xl text-navy-900 mb-2">Lead Delivery Settings</h1>
        <p className="text-navy-600 font-body">
          Adjust how quickly you want to receive your leads. You can change this anytime.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Settings Panel */}
        <div className="card-elevated">
          <h2 className="font-display text-xl text-navy-900 mb-6">Delivery Speed</h2>

          {/* Pause Toggle */}
          <div className="mb-8 p-4 bg-cream-50 rounded-lg border border-cream-200">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isPaused}
                onChange={(e) => setIsPaused(e.target.checked)}
                className="w-5 h-5 text-coral-500 rounded focus:ring-coral-500"
              />
              <div className="ml-3">
                <span className="font-medium text-navy-900">Pause Lead Delivery</span>
                <p className="text-sm text-navy-600 font-body">
                  Temporarily stop receiving new leads
                </p>
              </div>
            </label>
          </div>

          {/* Leads Per Week Slider */}
          {!isPaused && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium text-navy-900">Leads Per Week</h3>
                <span className="text-2xl font-display text-highlight-600">{leadsPerWeek}</span>
              </div>
              <p className="text-sm text-navy-600 mb-4 font-body">
                Choose how many leads you want to receive each week based on your capacity.
              </p>

              <div className="mb-6">
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={leadsPerWeek}
                  onChange={(e) => setLeadsPerWeek(parseInt(e.target.value))}
                  className="w-full h-2 bg-cream-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between mt-2 text-sm text-navy-500 font-body">
                  <span>1/week</span>
                  <span>10/week</span>
                  <span>20/week</span>
                </div>
              </div>

              {/* Warning for high volume */}
              {leadsPerWeek >= 8 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="text-amber-800 font-body">
                      <strong>Heads up!</strong> {leadsPerWeek} leads per week is a high volume. Make sure you have the capacity to follow up with all of them.
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Save Button */}
          <div className="mt-8">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full py-3 bg-coral-500 hover:bg-coral-600 text-white font-medium rounded transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>

            {saveMessage && (
              <div className={`mt-4 p-3 rounded text-center font-body ${
                saveMessage.includes('Error') 
                  ? 'bg-red-50 text-red-800 border border-red-200' 
                  : 'bg-green-50 text-green-800 border border-green-200'
              }`}>
                {saveMessage}
              </div>
            )}
          </div>
        </div>

        {/* Current Status Panel */}
        <div>
          <div className="card-elevated bg-navy-900 text-white">
            <h2 className="font-display text-xl mb-6">Current Status</h2>
            
            {advisorData && (
              <div className="space-y-4">
                <div className="flex justify-between pb-4 border-b border-navy-700">
                  <span className="text-navy-300 font-body">Leads in Balance</span>
                  <span className="font-medium text-2xl text-white">{advisorData.balance_leads || 0}</span>
                </div>

                <div className="flex justify-between pb-4 border-b border-navy-700">
                  <span className="text-navy-300 font-body">Current Setting</span>
                  <span className="font-medium">
                    {(advisorData.leads_per_week === 0 || isPaused) ? 'Paused' : `${advisorData.leads_per_week}/week`}
                  </span>
                </div>

                {deliveryWeeks && !isPaused && (
                  <div className="flex justify-between pb-4 border-b border-navy-700">
                    <span className="text-navy-300 font-body">Estimated Delivery</span>
                    <span className="font-medium">{deliveryWeeks} week{deliveryWeeks !== 1 ? 's' : ''}</span>
                  </div>
                )}

                {isPaused && (
                  <div className="bg-amber-900/30 border border-amber-500/50 rounded-lg p-4">
                    <p className="text-amber-200 text-sm font-body">
                      Lead delivery is currently paused. You won't receive any new leads until you unpause.
                    </p>
                  </div>
                )}

                {!isPaused && deliveryWeeks && (
                  <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-4 mt-4">
                    <p className="text-blue-200 text-sm font-body">
                      <strong>After saving:</strong> Your {advisorData.balance_leads} remaining lead{advisorData.balance_leads !== 1 ? 's' : ''} will be delivered at {leadsPerWeek} per week over approximately {deliveryWeeks} week{deliveryWeeks !== 1 ? 's' : ''}.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-cream-50 rounded-lg">
            <h3 className="font-medium text-navy-900 mb-2 flex items-center">
              <svg className="w-5 h-5 text-highlight-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              How This Works
            </h3>
            <ul className="text-sm text-navy-600 space-y-2 font-body">
              <li>• Changes take effect immediately</li>
              <li>• Your priority in the rotation adjusts automatically</li>
              <li>• You can change this as often as you need</li>
              <li>• Pausing stops all new lead assignments</li>
            </ul>
          </div>
        </div>
      </div>

      {/* SMS Notifications Section */}
      <div className="mt-8">
        <div className="card-elevated">
          <h2 className="font-display text-xl text-navy-900 mb-2">SMS Lead Notifications</h2>
          <p className="text-navy-600 font-body text-sm mb-6">
            Get a text message whenever a new lead is assigned to you so you never miss one, even if the email goes to spam.
          </p>

          {/* Phone Number */}
          <div className="mb-5">
            <label htmlFor="phone" className="block text-sm font-medium text-navy-700 mb-2">
              Mobile Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={handlePhoneChange}
              placeholder="(555) 123-4567"
              className={`input max-w-sm ${phone && !isPhoneValid() ? 'border-red-400 focus:ring-red-400' : ''}`}
            />
            {phone && !isPhoneValid() ? (
              <p className="text-xs text-red-500 mt-1 font-body">
                Please enter a complete 10-digit phone number.
              </p>
            ) : (
              <p className="text-xs text-navy-500 mt-1 font-body">
                US or Canadian mobile number. The +1 country code is added automatically.
              </p>
            )}
          </div>

          {/* SMS Consent Checkbox */}
          <div className="mb-5 p-4 bg-cream-50 rounded-lg border border-cream-200">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                className="w-5 h-5 text-coral-500 rounded focus:ring-coral-500 mt-0.5"
              />
              <div className="ml-3">
                <span className="font-medium text-navy-900 text-sm">
                  Yes, I would like to receive automated text messages from The Deolix Academy notifying me when a new lead has been assigned to my account. I understand I will receive one text message for each lead I receive.
                </span>
              </div>
            </label>
          </div>

          {/* Disclosures */}
          <div className="text-xs text-navy-500 font-body space-y-1 mb-5">
            <p><strong>Message Frequency:</strong> You will receive one text message per lead assigned to your account.</p>
            <p><strong>Standard Rates:</strong> Message and data rates may apply depending on your mobile phone service plan.</p>
            <p><strong>Help & Stop:</strong> Reply HELP for help or STOP to cancel any time.</p>
            <p>
              By providing your phone number and checking the box above, you agree to receive text messages from The Deolix Academy. Consent is not required to make a purchase.{' '}
              <a href="https://join.thedeolixacademy.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-coral-500 hover:text-coral-600 underline">Terms of Service</a>
              {' | '}
              <a href="https://join.thedeolixacademy.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-coral-500 hover:text-coral-600 underline">Privacy Policy</a>
            </p>
          </div>

          {/* Save Button and Test SMS */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleSaveProfile}
              disabled={isSavingProfile}
              className="py-3 px-6 bg-coral-500 hover:bg-coral-600 text-white font-medium rounded transition-colors disabled:opacity-50"
            >
              {isSavingProfile ? 'Saving...' : 'Save Phone Settings'}
            </button>

            {advisorData?.sms_consent && advisorData?.phone && (
              <button
                onClick={handleSendTestSMS}
                disabled={isSendingTest}
                className="py-3 px-6 bg-navy-700 hover:bg-navy-800 text-white font-medium rounded transition-colors disabled:opacity-50"
              >
                {isSendingTest ? 'Sending...' : 'Send Test SMS'}
              </button>
            )}
          </div>

          {profileMessage && (
            <div className={`mt-4 p-3 rounded text-center font-body ${
              profileMessage.includes('Error') 
                ? 'bg-red-50 text-red-800 border border-red-200' 
                : 'bg-green-50 text-green-800 border border-green-200'
            }`}>
              {profileMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
